/**
 * sak-column-anchor.js — keep the activated control still while the PDP info column resizes (packet 169 H2).
 *
 * At ≥750 the info column (`.form__wrapper`) is `position: sticky` inside `product-sticky`. Once its bottom has reached
 * the container's end a sticky box can only grow UPWARD, so opening anything in it — a tab, a collapsible, the add-on
 * panel — pushed the page up (the control the shopper just pressed jumped by the growth), and Chrome's scroll anchoring
 * added its own correction on top. This keeps the pressed control where it was: on a click or a keyboard activation of a
 * toggling control inside the column, its viewport top is recorded; for the rest of the expand/collapse (a
 * ResizeObserver on the column, released at `transitionend` or after 700 ms) every resize is answered with
 * an instant `scrollBy(0, nowTop − recordedTop)` (the page scrolls smoothly otherwise), measured from the real viewport position so scroll anchoring is cancelled too
 * (Safari, which may not anchor, gets the same result). Generic: any control that toggles something in the column.
 * Below 750 the column is in flow and nothing here runs. The WALL's colour swatches (a swap that replaces the zones),
 * variant inputs and page scrolls never arm it; the ADD-ON's swatches and size buttons (role="radio" inside .sak-add-on)
 * DO, because they show or hide the size row and resize the column in place (packet 186 G1).
 */
(() => {
  const MQ = window.matchMedia('(min-width: 750px)');
  const COLUMN = '.form__wrapper';
  const CONTROL = '[aria-expanded], [role="checkbox"], [role="switch"], [data-tab], [data-tab-index], [data-collapsible-trigger], [data-accordion-trigger], summary, .tab-link, [data-toggle], .sak-add-on [role="radio"]';
  const EXCLUDE = 'a.sibling__link, [data-siblings-pdp-root], [data-product-variants], [data-add-to-cart], .swym-button';
  const HOLD_MS = 700;
  let active = null;

  function release() {
    if (!active) return;
    active.ro.disconnect();
    clearTimeout(active.timer);
    document.removeEventListener('transitionend', active.onTransitionEnd, true);
    active = null;
  }

  function arm(control) {
    if (!MQ.matches) return;
    const column = control.closest(COLUMN);
    if (!column) return;
    release();
    const recorded = control.getBoundingClientRect().top;
    const ro = new ResizeObserver(() => {
      if (!control.isConnected) { release(); return; }
      const delta = control.getBoundingClientRect().top - recorded;
      // instant: Broadcast's `scroll-behavior: smooth` would glide the correction over several frames
      if (Math.abs(delta) >= 0.5) window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
    });
    ro.observe(column);
    const onTransitionEnd = (e) => { if (column.contains(e.target)) setTimeout(release, 40); };
    document.addEventListener('transitionend', onTransitionEnd, true);
    const timer = setTimeout(release, HOLD_MS);
    active = { control, recorded, ro, timer, onTransitionEnd };
  }

  function controlFrom(target) {
    if (!(target instanceof Element)) return null;
    const control = target.closest(CONTROL);
    if (!control || !control.closest(COLUMN) || control.closest(EXCLUDE)) return null;
    return control;
  }

  document.addEventListener('click', (e) => { const c = controlFrom(e.target); if (c) arm(c); }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const c = controlFrom(e.target);
    if (c) arm(c);
  }, true);
})();
